import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Filter,
  Search,
  Trash2,
  Flag,
  TrendingUp,
  Users,
  Package,
  AlertOctagon,
  ArrowUpRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentModerationQueue = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentTab, setCurrentTab] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchQueue();
    fetchStats();
  }, [currentTab, currentPage]);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_URL}/api/ai/fraud/moderation-queue?status=${currentTab}&page=${currentPage}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setQueue(data.items || []);
        setTotalPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
      toast({
        title: 'Error',
        description: 'Failed to load moderation queue',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai/fraud/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleReview = async (action) => {
    if (!selectedFlag) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai/fraud/review/${selectedFlag.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action, notes: reviewNotes })
      });
      
      if (response.ok) {
        toast({
          title: 'Success',
          description: `Item ${action}ed successfully`
        });
        setShowReviewModal(false);
        setSelectedFlag(null);
        setReviewNotes('');
        fetchQueue();
        fetchStats();
      } else {
        throw new Error('Failed to review');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process review',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const getRiskBadge = (level) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200',
      minimal: 'bg-green-100 text-green-700 border-green-200'
    };
    return <Badge className={`${colors[level] || colors.minimal} border`}>{level?.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      reviewed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle },
      escalated: { color: 'bg-purple-100 text-purple-700', icon: ArrowUpRight }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const openReviewModal = (flag) => {
    setSelectedFlag(flag);
    setReviewNotes('');
    setShowReviewModal(true);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-4 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin/dashboard')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-red-600" />
                  Content Moderation Queue
                </h1>
                <p className="text-gray-500 text-sm">Review AI-flagged listings and users</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => {
                fetchQueue();
                fetchStats();
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Review</p>
                  <p className="text-3xl font-bold text-yellow-600">
                    {stats?.pending_review || 0}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Flagged Today</p>
                  <p className="text-3xl font-bold text-red-600">
                    {stats?.today || 0}
                  </p>
                </div>
                <Flag className="w-10 h-10 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">This Week</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats?.this_week || 0}
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Risk</p>
                  <p className="text-3xl font-bold text-red-700">
                    {stats?.by_risk_level?.high || 0}
                  </p>
                </div>
                <AlertOctagon className="w-10 h-10 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risk Level Distribution */}
        {stats?.by_risk_level && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Risk Level Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {['high', 'medium', 'low', 'minimal'].map(level => (
                  <div key={level} className="text-center">
                    <div className="text-2xl font-bold">{stats.by_risk_level[level] || 0}</div>
                    <div className="text-sm text-gray-500 capitalize">{level}</div>
                    <Progress 
                      value={((stats.by_risk_level[level] || 0) / (stats.total || 1)) * 100} 
                      className={`h-2 mt-2 ${level === 'high' ? '[&>div]:bg-red-500' : level === 'medium' ? '[&>div]:bg-yellow-500' : level === 'low' ? '[&>div]:bg-blue-500' : '[&>div]:bg-green-500'}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Queue Tabs */}
        <Tabs value={currentTab} onValueChange={(v) => { setCurrentTab(v); setCurrentPage(1); }}>
          <TabsList>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending ({stats?.by_status?.pending || 0})
            </TabsTrigger>
            <TabsTrigger value="reviewed" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Reviewed
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Rejected
            </TabsTrigger>
            <TabsTrigger value="escalated" className="flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4" />
              Escalated
            </TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="mt-6">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                    <p className="text-gray-500 mt-2">Loading...</p>
                  </div>
                ) : queue.length === 0 ? (
                  <div className="p-12 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">All Clear!</h3>
                    <p className="text-gray-500">No {currentTab} items in the queue</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {queue.map((flag) => (
                      <div key={flag.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-medium text-gray-900">
                                {flag.listing_title || 'Unknown Listing'}
                              </h3>
                              {getRiskBadge(flag.risk_level)}
                              {getStatusBadge(flag.status)}
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                              <span>Risk Score: <strong className="text-gray-900">{flag.risk_score}/100</strong></span>
                              <span>•</span>
                              <span>Listing ID: {flag.listing_id?.slice(0, 8)}...</span>
                              <span>•</span>
                              <span>{new Date(flag.created_at).toLocaleString()}</span>
                            </div>
                            
                            {/* Flags */}
                            <div className="space-y-2">
                              {flag.flags?.map((f, idx) => (
                                <div key={idx} className="flex items-start gap-2 p-2 bg-red-50 rounded text-sm">
                                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-medium text-red-700">{f.name}</span>
                                    {f.details?.reason && (
                                      <p className="text-red-600 text-xs mt-0.5">{f.details.reason}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {flag.reviewed_by && (
                              <p className="text-xs text-gray-400 mt-2">
                                Reviewed by {flag.reviewed_by} on {new Date(flag.reviewed_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {flag.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 hover:bg-green-50"
                                  onClick={() => openReviewModal(flag)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => openReviewModal(flag)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`/listing/${flag.listing_id}`, '_blank')}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Flagged Item</DialogTitle>
            <DialogDescription>
              Take action on this flagged listing
            </DialogDescription>
          </DialogHeader>
          
          {selectedFlag && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium">{selectedFlag.listing_title}</h4>
                <div className="flex items-center gap-2 mt-2">
                  {getRiskBadge(selectedFlag.risk_level)}
                  <span className="text-sm text-gray-500">Score: {selectedFlag.risk_score}/100</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Review Notes (optional)</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  className="mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReviewModal(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleReview('escalate')}
              disabled={processing}
              className="text-purple-600"
            >
              <ArrowUpRight className="w-4 h-4 mr-1" />
              Escalate
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReview('reject')}
              disabled={processing}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject Listing
            </Button>
            <Button
              onClick={() => handleReview('approve')}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentModerationQueue;
